import React, { useMemo, useState } from "react";
import { AlertTriangle, FileDown, Send, CheckCircle2, Loader2, X, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/format";

/**
 * Rent Increase notice modal.
 * Props:
 *  - tenant: { id, full_name, email, monthly_rent, increase_percent, next_increase_date }
 *  - onClose()
 *  - onApplied()  called after "Apply increase" succeeds
 */
export default function IncreaseNoticeModal({ tenant, onClose, onApplied }) {
  const [percent, setPercent] = useState(Number(tenant.increase_percent || 0));
  const [effectiveDate, setEffectiveDate] = useState(
    tenant.next_increase_date || new Date().toISOString().slice(0, 10)
  );
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);

  const currentRent = Number(tenant.monthly_rent || 0);
  const newRent = useMemo(() => {
    return Math.round(currentRent * (1 + Number(percent || 0) / 100) * 100) / 100;
  }, [currentRent, percent]);

  const qs = `effective_date=${encodeURIComponent(effectiveDate)}&percent=${encodeURIComponent(percent)}`;

  const download = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("roost_token");
      const res = await fetch(`${API}/tenants/${tenant.id}/increase-notice?${qs}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("PDF error");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `increase-notice-${tenant.full_name.replace(/\s+/g, "_")}-${effectiveDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Notice downloaded");
    } catch {
      toast.error("Could not generate notice");
    } finally {
      setDownloading(false);
    }
  };

  const email = async () => {
    if (!tenant.email) { toast.error("Tenant has no email on file"); return; }
    setEmailing(true);
    try {
      const { data } = await api.post(`/tenants/${tenant.id}/increase-notice/email?${qs}`);
      if (data.status === "sent") toast.success(`Notice emailed to ${tenant.email}`);
      else if (data.status === "queued") toast.message("Notice logged (add RESEND_API_KEY to send live email)");
      else toast.error("Email failed");
    } catch {
      toast.error("Could not send notice");
    } finally {
      setEmailing(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(`/tenants/${tenant.id}/apply-increase`, {
        percent: Number(percent),
        effective_date: effectiveDate,
      });
      toast.success(`Rent updated to ${formatCurrency(data.new_rent)} · ${data.regenerated_payments} future payment(s) refreshed`);
      onApplied?.();
      onClose?.();
    } catch (e) {
      toast.error("Could not apply increase");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="increase-notice-modal">
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-xl fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-700 grid place-items-center">
              <TrendingUp className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg">Notice of Rental Increase</h3>
              <p className="text-xs text-slate-500">{tenant.full_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Inputs */}
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Increase %</span>
              <input
                type="number" step="0.1" min="0" value={percent}
                onChange={(e) => setPercent(e.target.value)}
                data-testid="increase-percent-input"
                className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Effective from</span>
              <input
                type="date" value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                data-testid="increase-effective-date-input"
                className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm"
              />
            </label>
          </div>

          {/* Live preview */}
          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
            <Row label="Current monthly rent" value={formatCurrency(currentRent)} />
            <Row label="Increase" value={`+${Number(percent || 0).toFixed(2)}%`} accent="indigo" />
            <Row label="New monthly rent" value={formatCurrency(newRent)} bold accent="emerald" />
            <Row label="Effective from" value={formatDate(effectiveDate)} />
          </div>

          {/* Actions row 1: send / download */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={download} disabled={downloading}
              data-testid="increase-download-btn"
              className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50 transition disabled:opacity-60"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Download PDF
            </button>
            <button
              onClick={email} disabled={emailing || !tenant.email}
              data-testid="increase-email-btn"
              title={tenant.email ? `Email to ${tenant.email}` : "Tenant has no email on file"}
              className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50 transition disabled:opacity-40"
            >
              {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Email to tenant
            </button>
          </div>

          {/* Confirm apply */}
          <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-900">
                Applying the increase updates <b>{tenant.full_name}</b>'s rent to <b>{formatCurrency(newRent)}</b> starting {formatDate(effectiveDate)} and regenerates every future unpaid rent-roll entry at the new amount. Paid entries are untouched.
              </div>
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmApply}
                onChange={(e) => setConfirmApply(e.target.checked)}
                data-testid="increase-confirm-checkbox"
                className="mt-0.5 accent-slate-900"
              />
              <span>I confirm the <b>{Number(percent || 0).toFixed(2)}%</b> increase on <b>{formatDate(effectiveDate)}</b>.</span>
            </label>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Close</button>
          <button
            onClick={apply}
            disabled={!confirmApply || applying || Number(percent) <= 0}
            data-testid="increase-apply-btn"
            className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Apply increase
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold = false, accent }) {
  const color = accent === "emerald" ? "text-emerald-700" : accent === "indigo" ? "text-indigo-700" : "text-slate-900";
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-sm text-slate-500">{label}</span>
      <span className={`font-mono-plex ${bold ? "font-display text-xl font-semibold" : "text-sm"} ${color}`}>{value}</span>
    </div>
  );
}
